import React from "react";
import { Box, CircularProgress, Typography, useTheme } from "@mui/material";
import DescriptionOutlinedIcon from "@mui/icons-material/DescriptionOutlined";
import ImageOutlinedIcon from "@mui/icons-material/ImageOutlined";
import { splitTextByVariableTokens } from "../../../../../../features/templates/utils/variableTokens";

const IMAGE_EXTENSIONS = [".jpg", ".jpeg", ".png", ".gif", ".webp", ".bmp", ".svg"];
const PDF_EXTENSIONS = [".pdf"];

const ensureArray = (value) => {
  if (!value) return [];
  return Array.isArray(value) ? value : [value];
};

const pick = (obj, keys) => {
  if (!obj || typeof obj !== "object") return undefined;
  for (const key of keys) {
    if (obj[key] !== undefined && obj[key] !== null) return obj[key];
  }
  return undefined;
};

const parseMediaKind = (item) => {
  const type = String(
    pick(item, ["kind", "mediaType", "type", "fileType", "mimeType", "mimetype"]) || "",
  ).toLowerCase();

  const url = String(pick(item, ["url", "fileUrl", "src", "link", "path"]) || "").toLowerCase();
  const name = String(pick(item, ["name", "fileName", "filename", "title"]) || "").toLowerCase();
  const source = `${type} ${url} ${name}`;

  if (source.includes("pdf") || PDF_EXTENSIONS.some((ext) => source.includes(ext))) {
    return "pdf";
  }

  if (
    source.includes("image") ||
    source.includes("img") ||
    IMAGE_EXTENSIONS.some((ext) => source.includes(ext))
  ) {
    return "image";
  }

  return null;
};

const normalizeMediaItem = (item) => {
  if (!item) return null;

  if (typeof item === "string") {
    const inferredKind = parseMediaKind({ url: item });
    if (!inferredKind) return null;
    return {
      id: item,
      kind: inferredKind,
      url: item,
      name: inferredKind === "pdf" ? "Documento PDF" : "Imagem",
    };
  }

  if (typeof item !== "object") return null;

  const kind = parseMediaKind(item);
  if (!kind) return null;

  const url = pick(item, ["url", "fileUrl", "src", "link", "path", "content"]) || "";
  const name = pick(item, ["name", "fileName", "filename", "title"]) || (kind === "pdf" ? "Documento PDF" : "Imagem");

  return {
    id: pick(item, ["id", "_id"]) || `${kind}-${name}-${url}`,
    kind,
    url,
    name,
    uploading: Boolean(item.uploading),
    isDynamicVariable: /\{\{.*\}\}/.test(String(url || "")),
  };
};

const getMessageMedia = (message) => {
  if (!message) return [];
  const nestedMessage =
    message.message && typeof message.message === "object"
      ? message.message
      : {};
  const header =
    message.header && typeof message.header === "object"
      ? message.header
      : nestedMessage.header && typeof nestedMessage.header === "object"
        ? nestedMessage.header
        : null;

  const rawMedia = [
    ...ensureArray(message.media),
    ...ensureArray(message.medias),
    ...ensureArray(message.attachments),
    ...ensureArray(message.files),
    ...ensureArray(message.attachment),
    ...ensureArray(message.file),
    ...ensureArray(message.image),
    ...ensureArray(message.images),
    ...ensureArray(message.document),
    ...ensureArray(message.documents),
    ...ensureArray(nestedMessage.media),
    ...ensureArray(nestedMessage.medias),
    ...ensureArray(nestedMessage.attachments),
    ...ensureArray(nestedMessage.files),
    ...ensureArray(nestedMessage.attachment),
    ...ensureArray(nestedMessage.file),
    ...ensureArray(nestedMessage.image),
    ...ensureArray(nestedMessage.images),
    ...ensureArray(nestedMessage.document),
    ...ensureArray(nestedMessage.documents),
  ];

  if (header?.type || header?.content) {
    rawMedia.push({
      type: header.type,
      content: header.content,
      name: header.name || header.fileName || header.filename,
      uploading: Boolean(header.uploading),
    });
  }

  const fromType = normalizeMediaItem({
    type: message.type || nestedMessage.type,
    content: message.content || nestedMessage.content,
  });

  if (fromType) {
    rawMedia.push({
      type: message.type,
      content: message.content,
    });
  }

  return rawMedia.map(normalizeMediaItem).filter(Boolean);
};

const MessageContent = ({ message, channel, mediaActions }) => {
  const theme = useTheme();
  const isFromMe = message.fromMe;
  const isWhatsappLayout =
    channel === "whatsapp_api" || channel === "whatsapp_web" || channel === "sms";
  const hasText = Boolean(message.content && String(message.content).trim());
  const medias = channel === "whatsapp_api" ? getMessageMedia(message) : [];
  const textParts = splitTextByVariableTokens(message.content);

  return (
    <Box
      sx={{
        px: isWhatsappLayout ? 0 : 1.5,
        py: isWhatsappLayout ? 0 : 1,
        borderRadius: 2,
        backgroundColor: isWhatsappLayout
          ? "transparent"
          : isFromMe
            ? theme.palette.success.light
            : theme.palette.background.paper,
        border: isWhatsappLayout
          ? "1px solid transparent"
          : `1px solid ${theme.palette.divider}`,
        minHeight: "auto",
      }}
    >
      {!!medias.length && (
        <Box sx={{ mb: hasText ? 1 : 0, display: "flex", flexDirection: "column", gap: 1 }}>
          {medias.map((media, index) =>
            media.kind === "image" ? (
              <Box
                key={media.id}
                sx={{
                  width: "100%",
                  maxHeight: 220,
                  borderRadius: 1.5,
                  backgroundColor: "#eef2f7",
                  position: "relative",
                  overflow: "hidden",
                }}
              >
                {index === 0 && mediaActions}
                {media.uploading ? (
                  <Box
                    sx={{
                      width: "100%",
                      minHeight: 190,
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      backgroundColor: "#d9dce2",
                    }}
                  >
                    <CircularProgress size={34} thickness={4} />
                  </Box>
                ) : media.isDynamicVariable ? (
                  <Box
                    sx={{
                      width: "100%",
                      minHeight: 140,
                      display: "flex",
                      flexDirection: "column",
                      alignItems: "center",
                      justifyContent: "center",
                      gap: 0.7,
                      color: "#7f8ea4",
                      backgroundColor: "#dde3eb",
                    }}
                  >
                    <ImageOutlinedIcon sx={{ fontSize: 40 }} />
                    <Typography variant="caption" sx={{ fontWeight: 600 }}>
                      {"{{Link_Imagem}}"}
                    </Typography>
                  </Box>
                ) : (
                  <Box
                    component="img"
                    src={media.url}
                    alt={media.name || "Imagem do modelo"}
                    sx={{
                      width: "100%",
                      maxHeight: 220,
                      objectFit: "cover",
                      display: "block",
                    }}
                  />
                )}
              </Box>
            ) : (
              <Box
                key={media.id}
                sx={{
                  width: "100%",
                  minHeight: 180,
                  borderRadius: 1.5,
                  backgroundColor: "#dde1e6",
                  display: "flex",
                  justifyContent: "center",
                  alignItems: "center",
                  position: "relative",
                }}
              >
                {index === 0 && mediaActions}
                {media.uploading ? (
                  <CircularProgress size={34} thickness={4} />
                ) : (
                  <Box sx={{ position: "relative", width: 86, height: 96 }}>
                    <DescriptionOutlinedIcon
                      sx={{
                        fontSize: 96,
                        color: "#c4c9cf",
                      }}
                    />
                    <Box
                      sx={{
                        position: "absolute",
                        left: "50%",
                        bottom: 8,
                        transform: "translateX(-50%)",
                        px: 1.5,
                        py: 0.4,
                        borderRadius: 0.5,
                        backgroundColor: "#ef4e3f",
                      }}
                    >
                      <Typography sx={{ color: "#fff", fontWeight: 700, fontSize: 16, lineHeight: 1 }}>
                        PDF
                      </Typography>
                    </Box>
                  </Box>
                )}

                {!!media.name && !media.uploading && (
                  <Typography
                    variant="caption"
                    sx={{
                      position: "absolute",
                      left: 10,
                      right: 10,
                      bottom: 8,
                      textAlign: "center",
                      color: "#68707a",
                      whiteSpace: "nowrap",
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                    }}
                  >
                    {media.name}
                  </Typography>
                )}
              </Box>
            ),
          )}
        </Box>
      )}

      {hasText && (
        <Typography
          component="div"
          variant="body2"
          sx={{
            overflowY: "auto",
            whiteSpace: "pre-wrap",
            wordBreak: "break-word",
            lineHeight: 1.4,
            color: isWhatsappLayout ? "#2f3945" : "inherit",

            scrollbarWidth: "thin",
            scrollbarColor: "#bbb transparent",
            "&::-webkit-scrollbar": { width: 6 },
            "&::-webkit-scrollbar-thumb": {
              backgroundColor: "#bbb",
              borderRadius: 3,
            },
          }}
        >
          {textParts.map((part) => (
            <Box
              key={part.key}
              component="span"
              sx={
                part.state === "valid"
                  ? {
                    backgroundColor: "#E9F1FF",
                    color: "#2F61BF",
                    borderRadius: "4px",
                    px: 0.35,
                  }
                  : part.state === "invalid"
                    ? {
                      backgroundColor: "#FFE9E5",
                      color: "#D1432F",
                      borderRadius: "4px",
                      px: 0.35,
                    }
                  : part.state === "warning"
                      ? {
                        backgroundColor: "#FFF3E8",
                        color: "#C77700",
                        borderRadius: "4px",
                        px: 0.35,
                      }
                    : part.state === "suggested"
                      ? {
                        backgroundColor: "#F1EDFF",
                        color: "#6C52C8",
                        borderRadius: "4px",
                        px: 0.35,
                      }
                  : undefined
              }
            >
              {part.text}
            </Box>
          ))}
        </Typography>
      )}
    </Box>
  );
};

export default MessageContent;
